import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import Loader from '../../../shared/components/ui/Loader';
import Modal from '../../../shared/components/ui/Modal';
import {
  EmptyRow,
  Field,
  StatusPill,
  Table,
  TableCard,
  TBody,
  Td,
  Th,
  THead
} from '../../../shared/components/ui/primitives';
import {
  createSiteWalletCategory,
  getSiteWalletCategories,
  updateSiteWalletCategory
} from '../../services/api/implementation/impl';
import { SmallButton, errorMessage, inputClass, looksLikeMaterial } from './siteWalletUi';

/**
 * The builder's expense categories. A monthly cap applies per wallet per
 * calendar month. Material is never a category; the server refuses one.
 */
const CategoriesPanel = () => {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(null);

  const categories = useQuery({
    queryKey: ['siteWalletCategories', 'all'],
    queryFn: () => getSiteWalletCategories({ all: 1 })
  });
  const rows = categories.data ?? [];

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['siteWalletCategories'] });
  const toggle = useMutation({
    mutationFn: (c) => updateSiteWalletCategory(c.id, { is_active: !c.is_active }),
    onSuccess: (c) => {
      refresh();
      toast.success(c?.is_active ? 'Category turned on' : 'Category turned off');
    },
    onError: (err) => toast.error(errorMessage(err, 'Could not update the category'))
  });

  if (categories.isLoading) return <Loader />;

  return (
    <>
      <TableCard
        title="Expense categories"
        action={
          <SmallButton size="md" tone="primary" onClick={() => setEditing({})}>
            <Plus size={14} /> Add category
          </SmallButton>
        }>
        <Table>
          <THead>
            <tr>
              <Th>Name</Th>
              <Th>Status</Th>
              <Th />
            </tr>
          </THead>
          <TBody>
            {!rows.length && <EmptyRow colSpan={3}>No categories yet.</EmptyRow>}
            {rows.map((c) => (
              <tr key={c.id} className={c.is_active ? '' : 'opacity-60'}>
                <Td className="font-medium text-gray-900">{c.name}</Td>
                <Td>
                  <StatusPill status={c.is_active ? 'ACTIVE' : 'INACTIVE'} dense>
                    {c.is_active ? 'Active' : 'Off'}
                  </StatusPill>
                </Td>
                <Td>
                  <div className="flex gap-2">
                    <SmallButton onClick={() => setEditing(c)}>
                      <Pencil size={12} /> Edit
                    </SmallButton>
                    <SmallButton disabled={toggle.isPending} onClick={() => toggle.mutate(c)}>
                      {c.is_active ? 'Turn off' : 'Turn on'}
                    </SmallButton>
                  </div>
                </Td>
              </tr>
            ))}
          </TBody>
        </Table>
      </TableCard>
      {editing && (
        <CategoryModal
          category={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            refresh();
            setEditing(null);
          }}
        />
      )}
    </>
  );
};

const CategoryModal = ({ category, onClose, onSaved }) => {
  const isNew = !category.id;
  const [name, setName] = useState(category.name ?? '');

  const save = useMutation({
    mutationFn: (payload) =>
      isNew ? createSiteWalletCategory(payload) : updateSiteWalletCategory(category.id, payload),
    onSuccess: () => {
      toast.success(isNew ? 'Category added' : 'Category updated');
      onSaved();
    },
    onError: (err) => toast.error(errorMessage(err, 'Could not save the category'))
  });

  const material = looksLikeMaterial(name);
  const valid = name.trim().length >= 2 && !material;

  return (
    <Modal name={isNew ? 'Add category' : 'Edit category'} onClose={onClose} width={36}>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate({ name: name.trim() });
        }}>
        <Field
          label="Name"
          required
          error={material ? 'Material goes through a material request, not the wallet.' : undefined}>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={80}
            className={inputClass}
            autoFocus
          />
        </Field>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700">
            Cancel
          </button>
          <button
            type="submit"
            disabled={!valid || save.isPending}
            className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default CategoriesPanel;
